import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Globe, Bot, TrendingUp, Headphones as HeadphonesIcon, Settings, ChevronDown, Check, ArrowRight, Phone } from 'lucide-react';
import { trackButtonClick, trackServiceInteraction } from '../lib/analytics';

type ViewMode = 'services' | 'pricing';

interface ServiceCategory {
  id: string;
  icon: React.ElementType;
  title: string;
  description: string;
  explanation: string;
  offerings: string[];
  typicalProjects: string[];
}

const ServiceSection = ({ category, isExpanded, onToggle }: {
  category: ServiceCategory;
  isExpanded: boolean;
  onToggle: () => void;
}) => {
  return (
    <div
      id={category.id}
      className="card-premium overflow-hidden scroll-mt-24"
    >
      <button
        onClick={() => {
          trackServiceInteraction(category.title, 'expand_toggle');
          onToggle();
        }}
        className="w-full p-6 lg:p-7 flex items-center justify-between text-left hover:bg-white/[0.03] transition-colors"
      >
        <div className="flex items-center">
          <div className="w-11 h-11 rounded-xl bg-primary/10 flex items-center justify-center mr-4">
            <category.icon className="h-5 w-5 text-primary/70" />
          </div>
          <div>
            <h3 className="text-[15px] font-medium text-white">{category.title}</h3>
            <p className="text-sm text-gray-500 mt-1">{category.description}</p>
          </div>
        </div>
        <ChevronDown
          className={`h-5 w-5 text-gray-500 transition-transform duration-300 flex-shrink-0 ml-4 ${
            isExpanded ? 'rotate-180' : ''
          }`}
        />
      </button>

      <div
        className={`overflow-hidden transition-all duration-500 ${
          isExpanded ? 'max-h-[1000px] opacity-100' : 'max-h-0 opacity-0'
        }`}
      >
        <div className="px-6 lg:px-7 pb-6 lg:pb-7 border-t border-white/[0.04] pt-5">
          <p className="text-gray-500 text-sm leading-relaxed mb-6">
            {category.explanation}
          </p>

          <div className="mb-6">
            <h4 className="text-white font-medium text-sm mb-3">What's included:</h4>
            <ul className="space-y-2.5">
              {category.offerings.map((offering, index) => (
                <li key={index} className="flex items-start text-sm text-gray-500">
                  <Check className="h-4 w-4 text-primary/50 mt-0.5 mr-2.5 flex-shrink-0" />
                  <span>{offering}</span>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h4 className="text-white font-medium text-sm mb-3">Typical projects include:</h4>
            <ul className="flex flex-wrap gap-2">
              {category.typicalProjects.map((project, index) => (
                <li
                  key={index}
                  className="tag-pill-subtle"
                >
                  {project}
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

interface PricingTier {
  name: string;
  icon: React.ElementType;
  price: string;
  priceNote: string;
  description: string;
  features: string[];
  highlighted?: boolean;
}

const PricingCard = ({ tier }: { tier: PricingTier }) => {
  const navigate = useNavigate();

  return (
    <div
      className={`card-premium p-6 lg:p-7 relative flex flex-col ${
        tier.highlighted ? 'border-primary/20 bg-primary/[0.03]' : ''
      }`}
    >
      {tier.highlighted && (
        <span className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1.5 text-xs font-medium text-white bg-primary/80 rounded-full">
          Popular
        </span>
      )}

      <div className="flex items-center mb-4">
        <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center mr-3">
          <tier.icon className="h-5 w-5 text-primary/70" />
        </div>
        <h3 className="text-[15px] font-medium text-white">{tier.name}</h3>
      </div>

      <div className="mb-4">
        <span className="text-2xl font-semibold text-white">{tier.price}</span>
        <span className="text-sm text-gray-500 ml-2">{tier.priceNote}</span>
      </div>

      <p className="text-sm text-gray-500 mb-6 leading-relaxed">{tier.description}</p>

      <ul className="space-y-3 mb-6 flex-grow">
        {tier.features.map((feature, index) => (
          <li key={index} className="flex items-start text-sm text-gray-500">
            <Check className="h-4 w-4 text-primary/50 mt-0.5 mr-2.5 flex-shrink-0" />
            <span>{feature}</span>
          </li>
        ))}
      </ul>

      <button
        onClick={() => {
          trackButtonClick(`Get Quote - ${tier.name}`, 'Pricing');
          navigate('/contact');
        }}
        className={`w-full py-3 text-sm font-medium rounded-xl transition-all duration-300 ${
          tier.highlighted
            ? 'btn-primary'
            : 'bg-white/[0.03] text-white hover:bg-white/[0.06] border border-white/[0.06]'
        }`}
      >
        Request a Quote
      </button>
    </div>
  );
};

const ServicesPage = () => {
  const [viewMode, setViewMode] = useState<ViewMode>('services');
  const [expandedCategories, setExpandedCategories] = useState<Set<string>>(new Set());
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const hash = location.hash.replace('#', '');
    if (hash) {
      setExpandedCategories(new Set([hash]));
      setTimeout(() => {
        const element = document.getElementById(hash);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth' });
        }
      }, 100);
    }
  }, [location.hash]);

  const toggleCategory = (id: string) => {
    setExpandedCategories((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const serviceCategories: ServiceCategory[] = [
    {
      id: 'websites',
      icon: Globe,
      title: 'Websites & Web Platforms',
      description: 'Modern, fast websites that convert visitors into customers',
      explanation: 'We build websites that look professional, load fast, and actually help your business grow. Every site is mobile-friendly, optimised for search engines (SEO), and designed to make it easy for visitors to take action.',
      offerings: [
        'Custom website design and development',
        'Mobile-responsive layouts',
        'SEO optimisation (search engine friendly)',
        'Contact forms and lead capture',
        'Content management systems (easy to update yourself)',
        'E-commerce functionality (online stores)',
        'Blog and content sections',
        'Analytics and tracking setup',
      ],
      typicalProjects: [
        'Business websites',
        'Landing pages',
        'Portfolio sites',
        'E-commerce stores',
        'Membership sites',
        'Booking platforms',
      ],
    },
    {
      id: 'applications',
      icon: Settings,
      title: 'Applications & SaaS',
      description: 'Custom web applications built for your specific needs',
      explanation: 'When off-the-shelf software doesn\'t fit your business, we build custom applications that do exactly what you need. These are web-based tools your team or customers can use from any device.',
      offerings: [
        'Custom web application development',
        'User authentication and accounts',
        'Database design and management',
        'API development and integrations',
        'Dashboard and reporting features',
        'Multi-user access controls',
        'Cloud hosting and scaling',
        'Ongoing maintenance and updates',
      ],
      typicalProjects: [
        'Customer portals',
        'Internal business tools',
        'Booking systems',
        'Inventory management',
        'CRM systems',
        'Data dashboards',
      ],
    },
    {
      id: 'automation',
      icon: Bot,
      title: 'AI & Automation Systems',
      description: 'Intelligent systems that handle tasks 24/7',
      explanation: 'From chatbots that answer customer questions to automation that handles repetitive tasks, we build AI-powered systems that save you time and improve your customer experience.',
      offerings: [
        'AI chatbots (trained on your business)',
        'Customer support automation',
        'Lead qualification bots',
        'Booking and scheduling automation',
        'Email and workflow automation',
        'Data processing and analysis',
        'Integration with existing systems',
        '24/7 operation without staff',
      ],
      typicalProjects: [
        'Customer service chatbots',
        'Sales qualification bots',
        'Appointment booking systems',
        'FAQ automation',
        'Lead capture systems',
        'Process automation',
      ],
    },
    {
      id: 'marketing',
      icon: TrendingUp,
      title: 'Marketing, SEO & Growth',
      description: 'Get found online and turn visitors into customers',
      explanation: 'Having a great website means nothing if nobody can find it. We help you get discovered through search engines and social media, then convert that traffic into actual business.',
      offerings: [
        'Search engine optimisation (SEO)',
        'Content strategy and creation',
        'Social media management',
        'Google Business Profile setup',
        'Analytics and conversion tracking',
        'Email marketing setup',
        'Landing page optimisation',
        'Local SEO (Isle of Man & UK)',
      ],
      typicalProjects: [
        'SEO campaigns',
        'Content marketing',
        'Social media management',
        'Lead generation',
        'Conversion optimisation',
        'Brand awareness',
      ],
    },
    {
      id: 'support',
      icon: HeadphonesIcon,
      title: 'Ongoing Support & Management',
      description: 'Keep everything running smoothly',
      explanation: 'Once your systems are live, we can handle the ongoing maintenance, updates, and improvements so you can focus on running your business.',
      offerings: [
        'Website hosting and maintenance',
        'Security updates and monitoring',
        'Content updates and changes',
        'Performance optimisation',
        'Regular backups',
        'Technical support',
        'Feature additions and improvements',
        'Monthly reporting',
      ],
      typicalProjects: [
        'Monthly maintenance plans',
        'Hosting management',
        'Security monitoring',
        'Performance optimisation',
        'Ongoing development',
        'Technical consulting',
      ],
    },
  ];

  const pricingTiers: PricingTier[] = [
    {
      name: 'Basic Website',
      icon: Globe,
      price: 'From £200',
      priceNote: 'starting price',
      description: 'Perfect for simple landing pages and basic business sites.',
      features: [
        '1-2 page landing page',
        'Mobile-friendly design',
        'Contact form',
        'Basic SEO setup',
        'Fast loading times',
      ],
    },
    {
      name: 'Standard Website',
      icon: Globe,
      price: 'From £350',
      priceNote: 'fixed price',
      description: 'Full business website with custom design.',
      features: [
        'Up to 5 pages',
        'Custom design and branding',
        'Mobile-responsive',
        'SEO optimisation',
        'Analytics setup',
        'Content management system',
      ],
    },
    {
      name: 'E-commerce',
      icon: Settings,
      price: 'From £750',
      priceNote: 'depending on complexity',
      description: 'Full online store with payment processing.',
      features: [
        'Product catalogue',
        'Shopping cart and checkout',
        'Payment gateway integration',
        'Order management',
        'Inventory tracking',
        'Customer accounts',
      ],
      highlighted: true,
    },
    {
      name: 'AI Chatbot',
      icon: Bot,
      price: 'Custom Quote',
      priceNote: 'based on requirements',
      description: 'Intelligent chatbot trained on your business.',
      features: [
        'Custom AI training',
        'Business knowledge integration',
        '24/7 automated responses',
        'Booking system integration',
        'Lead capture',
        'Handoff to human support',
      ],
    },
    {
      name: 'Social Media',
      icon: TrendingUp,
      price: 'From £239',
      priceNote: 'per month',
      description: 'Complete social media management.',
      features: [
        'All major platforms',
        'Content creation',
        'Scheduling and posting',
        'Community management',
        'Monthly reporting',
        'Strategy optimisation',
      ],
    },
    {
      name: 'Ongoing Support',
      icon: HeadphonesIcon,
      price: 'From £50',
      priceNote: 'per month',
      description: 'Keep your systems running smoothly.',
      features: [
        'Website hosting',
        'Security updates',
        'Regular backups',
        'Content updates',
        'Technical support',
        'Performance monitoring',
      ],
    },
  ];

  const handleStartProject = () => {
    trackButtonClick('Start Your Project', 'Services');
    navigate('/contact');
  };

  const handleRequestQuote = () => {
    trackButtonClick('Request a Quote', 'Services');
    navigate('/contact');
  };

  return (
    <div className="min-h-screen pt-24 lg:pt-28">
      <section className="max-w-6xl mx-auto px-5 sm:px-6 lg:px-8 py-12 lg:py-16">
        <div className="page-header animate-fade-in">
          <h1>Services & Pricing</h1>
          <p>
            Everything from simple websites to complex AI systems. Clear pricing, no surprises.
          </p>
        </div>

        <div className="flex justify-center mb-12 lg:mb-16 animate-fade-in-delay-1" style={{ opacity: 0 }}>
          <div className="inline-flex bg-white/[0.03] rounded-xl p-1 border border-white/[0.06]">
            <button
              onClick={() => setViewMode('services')}
              className={`px-6 py-2.5 text-sm font-medium rounded-lg transition-all duration-300 ${
                viewMode === 'services'
                  ? 'bg-primary/90 text-white'
                  : 'text-gray-500 hover:text-white'
              }`}
            >
              Services
            </button>
            <button
              onClick={() => setViewMode('pricing')}
              className={`px-6 py-2.5 text-sm font-medium rounded-lg transition-all duration-300 ${
                viewMode === 'pricing'
                  ? 'bg-primary/90 text-white'
                  : 'text-gray-500 hover:text-white'
              }`}
            >
              Pricing
            </button>
          </div>
        </div>

        {viewMode === 'services' ? (
          <div className="space-y-4 animate-fade-in-delay-2" style={{ opacity: 0 }}>
            {serviceCategories.map((category) => (
              <ServiceSection
                key={category.id}
                category={category}
                isExpanded={expandedCategories.has(category.id)}
                onToggle={() => toggleCategory(category.id)}
              />
            ))}

            <div className="bg-white/[0.02] rounded-xl p-5 mt-10 border border-white/[0.04]">
              <p className="text-gray-500 text-sm text-center leading-relaxed">
                If you're not technical, don't worry — we'll guide you through every decision and explain everything clearly.
              </p>
            </div>

            <div className="text-center pt-8">
              <button onClick={handleStartProject} className="btn-primary">
                Start Your Project
                <ArrowRight className="ml-2 h-4 w-4 inline" />
              </button>
            </div>
          </div>
        ) : (
          <div className="animate-fade-in-delay-2" style={{ opacity: 0 }}>
            <div className="bg-white/[0.02] rounded-xl p-5 mb-10 border border-white/[0.04]">
              <p className="text-gray-500 text-sm text-center leading-relaxed">
                Prices below are starting points to provide clarity. Final costs depend on scope, integrations, automation level, and growth requirements.
              </p>
            </div>

            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8 mb-10">
              {pricingTiers.map((tier, index) => (
                <PricingCard key={index} tier={tier} />
              ))}
            </div>

            <div className="card-premium p-6 lg:p-8 mb-10">
              <h3 className="text-white font-medium mb-5">What affects pricing?</h3>
              <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
                {[
                  { title: 'Complexity', desc: 'Number of features and pages' },
                  { title: 'Integrations', desc: 'Connections to other systems' },
                  { title: 'Automation', desc: 'Level of AI and automation' },
                  { title: 'Scale', desc: 'Expected traffic and growth' },
                ].map((item, index) => (
                  <div key={index} className="text-center p-4 bg-white/[0.03] rounded-xl">
                    <h4 className="text-white text-sm font-medium mb-1">{item.title}</h4>
                    <p className="text-gray-500 text-xs">{item.desc}</p>
                  </div>
                ))}
              </div>
            </div>

            <div className="text-center space-y-4 sm:space-y-0 sm:flex sm:justify-center sm:gap-4">
              <button onClick={handleStartProject} className="btn-primary w-full sm:w-auto">
                Start Your Project
                <ArrowRight className="ml-2 h-4 w-4 inline" />
              </button>
              <button onClick={handleRequestQuote} className="btn-secondary w-full sm:w-auto">
                <Phone className="mr-2 h-4 w-4 inline" />
                Request a Quote
              </button>
            </div>
          </div>
        )}
      </section>

    </div>
  );
};

export default ServicesPage;
